import { NotificationRule } from '@eppendorf/vnls-notifications-utils';
import {
  Badge,
  Button,
  Dialog,
  Icon,
  IconSizeClasses,
  ToggleBadge,
} from '@eppendorf/vnls-react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePauseNotificationRule } from '$features/notifications/notification-rules.api';
import { useCanPauseNotificationRule } from '$features/notifications/use-can-pause-notification-rule';

import styles from './dialog-with-ul.module.scss';

export interface PauseNotificationRuleToggleProps {
  notificationRule: NotificationRule;
  bordered?: boolean;
}

export function PauseNotificationRuleToggle({
  notificationRule,
  bordered = false,
}: PauseNotificationRuleToggleProps) {
  const { t } = useTranslation();
  const { mutate: pauseRule, isPending } = usePauseNotificationRule();
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
  const canPause = useCanPauseNotificationRule(notificationRule);

  const isPaused = notificationRule.paused ?? false;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      pauseRule({ id: notificationRule.id, paused: false });
    } else {
      setIsPauseDialogOpen(true);
    }
  };

  const handleConfirmPause = () => {
    pauseRule(
      { id: notificationRule.id, paused: true },
      { onSettled: () => setIsPauseDialogOpen(false) },
    );
  };

  const statusBadge = (
    <Badge color={isPaused ? 'orange' : 'green'} bordered={bordered}>
      {t(isPaused ? 'notificationRules.paused' : 'notificationRules.active')}
    </Badge>
  );

  return (
    <>
      {canPause ? (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- prevent row click when toggling
        <span onClick={(e) => e.stopPropagation()}>
          <ToggleBadge
            checked={isPaused !== true}
            onChange={handleToggle}
            disabled={isPending}
            variant={bordered ? 'outline' : 'solid'}
            onColor="green"
            offColor="orange"
            activeLabel={t('notificationRules.active')}
            inactiveLabel={t('notificationRules.paused')}
            offIcon="pause"
          />
        </span>
      ) : (
        statusBadge
      )}

      {/* Portal-rendered dialog, but stopPropagation on overlay/content prevents row click */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- prevent row click from dialog */}
      <div onClick={(e) => e.stopPropagation()}>
        <Dialog
          isOpen={isPauseDialogOpen}
          onOpenChange={(open) => setIsPauseDialogOpen(open)}
          className="w-max-4xl"
        >
          <div className="flex flex__dir--column flex__ai--center text-align-center">
            <Icon
              name="info"
              className="bg-orange-400 m-bottom-xxl"
              size={IconSizeClasses.XLarge}
            />
            <Dialog.Title>
              {t('notificationRules.confirmPauseTitle', { name: notificationRule.name })}
            </Dialog.Title>
            <Dialog.Description />
            <div className={`${styles.dialogWithUl} m-top-xxs text-align-left w-full`}>
              <p>{t('notificationRules.confirmPauseDescription.top')}</p>
              <ul>
                <li>{t('notificationRules.confirmPauseDescription.bullet1')}</li>
                <li>{t('notificationRules.confirmPauseDescription.bullet2')}</li>
              </ul>
            </div>
          </div>
          <Dialog.Actions className="m-top-xxl">
            <Dialog.Close>
              <Button variant="secondary">{t('shared.cancel')}</Button>
            </Dialog.Close>
            <Button variant="primary" onClick={handleConfirmPause} disabled={isPending}>
              {t('notificationRules.pause')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </div>
    </>
  );
}
